import { ReactNode, useEffect, useRef, useState } from 'react'
import { Flex, Input, Text } from '@pancakeswap/uikit'
import useSWR from 'swr'
import { isAddress } from 'viem'
import styled from 'styled-components'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { useAllTokens } from 'hooks/Tokens'
import { useAllTokenBalances } from 'state/wallet/hooks'
import { fetcher, short } from '../config'
import { useTokenMeta } from '../hooks'
import { DexLogo } from '../pool-search/PoolSearch'
import { Chip } from '../ui'

/** A prize-token chip: the token's logo and its FULL symbol (never clipped, the chip keeps its width and the row wraps instead). */
export function PrizeChip({ address, on, onPick }: { address: string; on: boolean; onPick: () => void }) {
  const { symbol } = useTokenMeta(address, true)
  return (
    <Chip type="button" $on={on} onClick={onPick} style={{ flex: 'none', maxWidth: 'none', paddingLeft: 10 }} aria-pressed={on} data-testid="v3-prize-chip">
      <TokenLogo address={address} symbol={symbol} size={24} />
      <span style={{ whiteSpace: 'nowrap' }}>{symbol ?? short(address)}</span>
    </Chip>
  )
}

const Strip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 20px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(180, 200, 255, 0.16);
  background: rgba(255, 255, 255, 0.03);
  .label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: rgba(190, 205, 240, 0.7);
  }
  .logos {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 18px;
  }
`
/** The last prize chip: "Other" opens the token-address field (it is hidden under the listed chips). */
export function OtherChip({ on, onPick }: { on: boolean; onPick: () => void }) {
  return (
    <Chip type="button" $on={on} onClick={onPick} aria-pressed={on} style={{ flex: 'none' }} data-testid="v3-prize-other">
      Other
    </Chip>
  )
}

const Item = styled.span<{ $off: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: none;
  opacity: ${({ $off }) => ($off ? 0.4 : 1)};
`
const Ver = styled.span`
  display: inline-flex;
  gap: 3px;
  b {
    padding: 0 5px;
    min-height: 16px;
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    font-size: 10px;
    line-height: 14px;
    font-weight: 800;
    color: rgba(215, 243, 251, 0.9);
    background: rgba(127, 212, 232, 0.12);
    border: 1px solid rgba(127, 212, 232, 0.3);
  }
`

/** The exchanges the contract accepts: information only (nothing here is selectable). One quiet strip of real logos, each with a V2 / V3 badge for the versions it has; the name is the tooltip. */
export function DexRow({ dexes }: { dexes: Array<{ name: string; kind: string; enabled?: boolean }> }) {
  const brands: Array<{ brand: string; versions: string[]; enabled: boolean; names: string[] }> = []
  dexes.forEach((d) => {
    const brand = d.name.replace(/ V[23]$/, '')
    const v = /V([23])$/.test(d.name) ? `V${d.name.slice(-1)}` : d.kind
    let b = brands.find((x) => x.brand === brand)
    if (!b) {
      b = { brand, versions: [], enabled: false, names: [] }
      brands.push(b)
    }
    if (!b.versions.includes(v)) b.versions.push(v)
    b.names.push(d.name)
    if (d.enabled !== false) b.enabled = true
  })
  return (
    <Strip data-testid="v3-dexes" aria-label="Supported exchanges">
      <span className="label">Supported exchanges</span>
      <span className="logos">
        {brands.map((b) => (
          <Item key={b.brand} $off={!b.enabled} title={b.names.join(', ')} data-testid="v3-dex" data-brand={b.brand}>
            <DexLogo dex={b.names[0]} size={30} />
            <Ver>{b.versions.sort().map((v) => <b key={v}>{v}</b>)}</Ver>
          </Item>
        ))}
      </span>
    </Strip>
  )
}

const HintWrap = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  margin-left: 8px;
  button {
    all: unset;
    box-sizing: border-box;
    position: relative;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    font-family: Georgia, serif;
    font-style: italic;
    font-weight: 700;
    font-size: 14px;
    color: #d7f3fb;
    background: rgba(127, 212, 232, 0.14);
    border: 1px solid rgba(127, 212, 232, 0.5);
    &::after {
      content: '';
      position: absolute;
      inset: -10px;
    }
    &:hover {
      background: rgba(127, 212, 232, 0.26);
    }
    &:focus-visible {
      outline: 2px solid #7fd4e8;
      outline-offset: 2px;
    }
  }
  .pop {
    position: absolute;
    z-index: 30;
    top: calc(100% + 8px);
    left: -8px;
    width: 300px;
    max-width: calc(100vw - 56px);
    padding: 12px 14px;
    border-radius: 14px;
    font-family: inherit;
    font-style: normal;
    font-weight: 400;
    font-size: 13px;
    line-height: 19px;
    text-align: left;
    color: #e6ecff;
    background: #101736;
    border: 1px solid rgba(127, 212, 232, 0.4);
    box-shadow: 0 16px 36px rgba(0, 0, 0, 0.5);
  }
`

/** A small (i) next to a control: the explanation is one tap away and nowhere else. Closes on a tap outside and on Escape. The text stays in the page (hidden) so it can be read by tools. */
export function Hint({ children, label = 'More information', testId, align = 'left' }: { children: ReactNode; label?: string; testId?: string; align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return undefined
    const close = (e: Event) => {
      if (e.type === 'keydown' ? (e as KeyboardEvent).key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', close)
    }
  }, [open])
  return (
    <HintWrap ref={ref}>
      <button type="button" aria-label={label} aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid={testId ? `${testId}-btn` : 'v3-hint-btn'}>i</button>
      <span className="pop" role="note" hidden={!open} data-testid={testId} style={align === 'right' ? { left: 'auto', right: -8 } : undefined}>{children}</span>
    </HintWrap>
  )
}

/** The one question of a step: big, strong, with its (i). */
export const Headline = styled.h2`
  margin: 0 0 18px;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: -0.01em;
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 32px;
  }
`

/** A quiet text link in place of a button, for the secondary path of a step. */
export const QuietLink = styled.button`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSubtle};
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
`

const Rows = styled.ul`
  list-style: none;
  margin: 6px 0 0;
  padding: 4px;
  display: grid;
  gap: 2px;
  border-radius: 14px;
  border: 1px solid rgba(180, 200, 255, 0.18);
  background: rgba(255, 255, 255, 0.03);
  li button {
    all: unset;
    box-sizing: border-box;
    width: 100%;
    min-height: 48px;
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
    border-radius: 10px;
    cursor: pointer;
    &:hover,
    &:focus-visible {
      background: rgba(127, 212, 232, 0.14);
    }
    &:focus-visible {
      outline: 2px solid #7fd4e8;
    }
  }
  .sym {
    font-weight: 800;
    font-size: 15px;
  }
  .addr {
    font-family: 'Martian Mono', ui-monospace, monospace;
    font-size: 12px;
    color: rgba(190, 205, 240, 0.7);
  }
`

type Found = { address: string; symbol: string; name?: string; balance?: string }

/**
 * The prize token behind "Other": type a symbol or a name (our token list and the pool search find it) or paste an address; tap a row to choose it. The field holds
 * what was typed, the chosen address goes to `onPick` (an empty string while nothing valid is chosen), so the form's own validation shows the rest.
 */
export function PrizeTokenSearch({ value, onPick, inputProps }: { value: string; onPick: (address: string) => void; inputProps?: Record<string, unknown> }) {
  const [q, setQ] = useState(value)
  const [open, setOpen] = useState(false)
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])
  const typed = debounced.length >= 2 && !isAddress(debounced) && !/^0x/i.test(debounced)
  const { data: list } = useSWR<{ items: Array<{ address: string; symbol: string; name: string }> }>(typed ? '/api/tokens/list' : null, fetcher, { revalidateOnFocus: false })
  const { data: found } = useSWR<{ tokens?: Array<{ address: string; symbol: string }> }>(typed ? `/api/competition-v3/pool-search?q=${encodeURIComponent(debounced)}` : null, fetcher, { revalidateOnFocus: false, shouldRetryOnError: false })
  const needle = debounced.toLowerCase()
  // what the connected wallet holds, from the same token lists and balances the Swap picker uses
  const allTokens = useAllTokens()
  const balances = useAllTokenBalances()
  const held: Found[] = Object.values(allTokens ?? {})
    .map((t) => ({ t, b: balances?.[t.address] }))
    .filter((x) => x.b && x.b.greaterThan(0))
    .map((x) => ({ address: x.t.address, symbol: x.t.symbol ?? short(x.t.address), name: x.t.name, balance: x.b!.toSignificant(6) }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
  const rows: Found[] = []
  const add = (r: Found) => {
    if (r.address && !rows.some((x) => x.address.toLowerCase() === r.address.toLowerCase())) rows.push(r)
  }
  held.filter((h) => !needle || h.symbol.toLowerCase().includes(needle) || (h.name ?? '').toLowerCase().includes(needle)).forEach(add)
  if (typed) {
    ;(list?.items ?? []).filter((i) => i.symbol?.toLowerCase().includes(needle) || i.name?.toLowerCase().includes(needle)).forEach(add)
    ;(found?.tokens ?? []).filter((t) => t.symbol?.toLowerCase().includes(needle)).forEach(add)
  }
  return (
    <div>
      <Input
        {...inputProps}
        value={q}
        style={{ height: 48, borderRadius: 14 }}
        placeholder="Symbol, name or address 0x…"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          const v = e.target.value
          setQ(v)
          setOpen(true)
          onPick(isAddress(v.trim()) ? v.trim() : '')
        }}
      />
      {open && (typed || (!q.trim() && held.length > 0)) && (
        <Rows data-testid="v3-token-results" style={{ maxHeight: 264, overflowY: 'auto' }}>
          {!q.trim() && <li style={{ padding: '6px 10px 2px', fontSize: 12, opacity: 0.7 }}>In your wallet</li>}
          {rows.slice(0, q.trim() ? 8 : 50).map((r) => (
            <li key={r.address}>
              <button type="button" data-testid="v3-token-row" onClick={() => { onPick(r.address); setQ(r.symbol); setOpen(false) }}>
                <TokenLogo address={r.address} symbol={r.symbol} size={28} />
                <span className="sym">{r.symbol}{r.name && r.name !== r.symbol ? <span style={{ fontWeight: 400, opacity: 0.7 }}> · {r.name}</span> : null}</span>
                <span className="addr" data-testid={r.balance ? 'v3-token-balance' : undefined}>{r.balance ?? short(r.address)}</span>
              </button>
            </li>
          ))}
          {rows.length === 0 && typed && <li style={{ padding: '12px 10px', fontSize: 13, opacity: 0.7 }}>{found || list ? 'No token with that name here. Paste its address instead.' : 'Searching…'}</li>}
        </Rows>
      )}
    </div>
  )
}
